import { useState, useEffect } from 'react';
import { DocumentArrowUpIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import type { ContractTemplate, TemplateType, SharePointFile } from '@shared/types';
import { sharepointService } from '../../services/sharepointService';
import { SharePointFilePicker } from './SharePointFilePicker';
import { getExampleFilename } from '@shared/documentNamingUtils';

interface TemplateEditorProps {
  template: ContractTemplate | null;
  onSave: (template: Partial<ContractTemplate>) => void;
  onCancel: () => void;
  saving?: boolean;
}

// Generate preview filename for the pattern
function generatePreviewFilename(pattern: string): string {
  try {
    return getExampleFilename(pattern);
  } catch (error) {
    return 'Invalid pattern';
  }
}

export function TemplateEditor({ template, onSave, onCancel, saving = false }: TemplateEditorProps) {
  const [name, setName] = useState('');
  const [templateType, setTemplateType] = useState<TemplateType>('SOW');
  const [description, setDescription] = useState('');
  const [sharepointPath, setSharepointPath] = useState('');
  const [sharepointFileId, setSharepointFileId] = useState('');
  const [sharepointFileName, setSharepointFileName] = useState('');
  const [sharepointDriveId, setSharepointDriveId] = useState('');
  const [namingConvention, setNamingConvention] = useState('');
  const [selectedFile, setSelectedFile] = useState<SharePointFile | null>(null);
  const [hasScope, setHasScope] = useState(true);
  const [hasDeliverables, setHasDeliverables] = useState(true);
  const [hasMilestones, setHasMilestones] = useState(true);
  const [hasPaymentSchedule, setHasPaymentSchedule] = useState(true);
  const [hasLicensing, setHasLicensing] = useState(false);
  const [supportsRecurringPayments, setSupportsRecurringPayments] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (template) {
      setName(template.name);
      setTemplateType(template.templateType);
      setDescription(template.description || '');
      setSharepointPath(template.sharepointPath);
      setSharepointFileId(template.sharepointFileId || '');
      setSharepointFileName(template.sharepointFileName || '');
      setSharepointDriveId(template.sharepointDriveId || '');
      setNamingConvention(template.namingConvention || '');
      setHasScope(template.hasScope);
      setHasDeliverables(template.hasDeliverables);
      setHasMilestones(template.hasMilestones);
      setHasPaymentSchedule(template.hasPaymentSchedule);
      setHasLicensing(template.hasLicensing || false);
      setSupportsRecurringPayments(template.supportsRecurringPayments);
      setIsActive(template.isActive);
    } else {
      resetForm();
    }
  }, [template]);

  const resetForm = () => {
    setName('');
    setTemplateType('SOW');
    setDescription('');
    setSharepointPath('');
    setSharepointFileId('');
    setSharepointFileName('');
    setSharepointDriveId('');
    setNamingConvention('');
    setSelectedFile(null);
    setHasScope(true);
    setHasDeliverables(true);
    setHasMilestones(true);
    setHasPaymentSchedule(true);
    setHasLicensing(false);
    setSupportsRecurringPayments(false);
    setIsActive(true);
    setUploadedFile(null);
  };

  const handleSharePointFileSelect = (file: SharePointFile | null) => {
    setSelectedFile(file);
    if (file) {
      setSharepointPath(file.path);
      setSharepointFileId(file.id);
      setSharepointFileName(file.name);
      setSharepointDriveId(file.driveId);
    } else {
      setSharepointPath('');
      setSharepointFileId('');
      setSharepointFileName('');
      setSharepointDriveId('');
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // ACORD uses a blank PDF as the stamping source; W9 is a static PDF
    // attached as-is to outbound emails; everything else is a Word
    // document for docxtemplater merging.
    const isPdf = templateType === 'ACORD' || templateType === 'W9';
    const validTypes = isPdf ? ['.pdf'] : ['.doc', '.docx'];
    const fileExt = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (!validTypes.includes(fileExt)) {
      toast.error(
        isPdf
          ? `Please upload a PDF for the ${templateType} template`
          : 'Please upload a Word document (.doc or .docx)',
      );
      return;
    }

    setUploadedFile(file);
    setUploading(true);
    const toastId = toast.loading('Uploading to SharePoint...');

    try {
      const result = await sharepointService.uploadFile('templates', file.name, file);
      setSharepointPath(result.path);
      setSharepointFileId(result.id || '');
      toast.success('Template uploaded to SharePoint!', { id: toastId });
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload to SharePoint. You can manually enter the path.', { id: toastId });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!sharepointFileId) {
      toast.error('Please select a SharePoint template file');
      return;
    }

    onSave({
      name,
      templateType,
      description,
      sharepointPath,
      sharepointFileId,
      sharepointFileName,
      sharepointDriveId,
      namingConvention,
      hasScope,
      hasDeliverables,
      hasMilestones,
      hasPaymentSchedule,
      hasLicensing,
      supportsRecurringPayments,
      isActive,
    });
  };

  // Auto-configure based on template type
  const handleTypeChange = (type: TemplateType) => {
    setTemplateType(type);
    
    // Auto-configure common settings per type
    switch (type) {
      case 'NDA':
        setHasScope(false);
        setHasDeliverables(false);
        setHasMilestones(false);
        setHasPaymentSchedule(false);
        setSupportsRecurringPayments(false);
        break;
      case 'PSA':
      case 'MSA':
        setHasScope(false);
        setHasDeliverables(false);
        setHasMilestones(false);
        setHasPaymentSchedule(false);
        setSupportsRecurringPayments(false);
        break;
      case 'License Agreement':
        setHasScope(true);
        setHasDeliverables(true);
        setHasMilestones(false);
        setHasPaymentSchedule(true);
        setSupportsRecurringPayments(true);
        break;
      case 'ACORD':
      case 'W9':
        // ACORD and W9 templates don't go through the contract wizard —
        // ACORD is generated per-client; W9 is a static attachment shared
        // across clients. Zero out every wizard flag.
        setHasScope(false);
        setHasDeliverables(false);
        setHasMilestones(false);
        setHasPaymentSchedule(false);
        setSupportsRecurringPayments(false);
        break;
      case 'SOW':
      default:
        setHasScope(true);
        setHasDeliverables(true);
        setHasMilestones(true);
        setHasPaymentSchedule(true);
        setSupportsRecurringPayments(false);
        break;
    }
  };

  return (
    <div className="bg-white shadow-sm rounded-lg p-6">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        {template ? 'Edit Template' : 'New Template'}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            Template Name *
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Standard Statement of Work"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            required
          />
        </div>

        <div>
          <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
            Template Type *
          </label>
          <select
            id="type"
            value={templateType}
            onChange={(e) => handleTypeChange(e.target.value as TemplateType)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="SOW">Statement of Work</option>
            <option value="NDA">Non-Disclosure Agreement</option>
            <option value="PSA">Professional Services Agreement</option>
            <option value="MSA">Master Service Agreement</option>
            <option value="Service Agreement">Service Agreement</option>
            <option value="License Agreement">License Agreement</option>
            <option value="ACORD">ACORD Insurance Certificate</option>
            <option value="W9">W-9 Form</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Brief description of when to use this template..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        <div>
          <label htmlFor="namingConvention" className="block text-sm font-medium text-gray-700 mb-1">
            File Naming Pattern
          </label>
          <input
            type="text"
            id="namingConvention"
            value={namingConvention}
            onChange={(e) => setNamingConvention(e.target.value)}
            placeholder="{client_short}_{template}_{date}"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 font-mono text-sm"
          />
          <div className="mt-2 p-3 bg-gray-50 rounded-md border border-gray-200">
            <p className="text-xs font-semibold text-gray-700 mb-2">Available Tokens:</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
              <div><code className="text-primary-600">{'{client}'}</code> - Full client name</div>
              <div><code className="text-primary-600">{'{client_short}'}</code> - Client short name</div>
              <div><code className="text-primary-600">{'{project}'}</code> - Project title</div>
              <div><code className="text-primary-600">{'{template}'}</code> - Template type</div>
              <div><code className="text-primary-600">{'{date}'}</code> - Date (YYYY-MM-DD)</div>
              <div><code className="text-primary-600">{'{year}'}</code> - Year (YYYY)</div>
              <div><code className="text-primary-600">{'{month}'}</code> - Month (MM)</div>
              <div><code className="text-primary-600">{'{day}'}</code> - Day (DD)</div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-300">
              <p className="text-xs font-semibold text-gray-700 mb-1">Examples:</p>
              <div className="space-y-1 text-xs text-gray-600">
                <div><code className="bg-white px-1 rounded">{'{client_short}_SOW_{date}'}</code> → MS_SOW_2025-10-11.docx</div>
                <div><code className="bg-white px-1 rounded">{'{project}_{year}_{month}'}</code> → SharePoint_Implementation_2025_10.docx</div>
                <div><code className="bg-white px-1 rounded">{'SOW_{client}_{date}'}</code> → SOW_Microsoft_2025-10-11.docx</div>
              </div>
            </div>
          </div>
          {namingConvention && (
            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-xs font-semibold text-blue-900">Preview:</p>
              <p className="text-xs text-blue-800 font-mono mt-1">
                {generatePreviewFilename(namingConvention)}
              </p>
            </div>
          )}
        </div>

        <div className="border-t pt-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">SharePoint Document</h3>
          
          {!template && (
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Upload Template to SharePoint
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-primary-500 transition-colors">
                <DocumentArrowUpIcon className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                <input
                  type="file"
                  accept={templateType === 'ACORD' || templateType === 'W9' ? '.pdf' : '.doc,.docx'}
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                  disabled={uploading || saving}
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer text-primary-600 hover:text-primary-700 font-medium"
                >
                  {uploading
                    ? 'Uploading...'
                    : templateType === 'ACORD'
                      ? 'Choose blank ACORD PDF'
                      : templateType === 'W9'
                        ? 'Choose W-9 PDF'
                        : 'Choose Word document'}
                </label>
                <p className="mt-1 text-xs text-gray-500">
                  {templateType === 'ACORD' || templateType === 'W9' ? '.pdf file' : '.doc or .docx files'}
                </p>
                {uploadedFile && (
                  <p className="mt-2 text-sm text-green-600">
                    ✓ {uploadedFile.name}
                  </p>
                )}
              </div>
            </div>
          )}

          <div>
            <SharePointFilePicker
              selectedFileId={sharepointFileId}
              onSelect={handleSharePointFileSelect}
            />
          </div>
        </div>

        <div className="border-t pt-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Template Configuration</h3>
          <p className="text-xs text-gray-600 mb-3">
            Configure what sections this template requires
          </p>
          
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={hasScope}
                onChange={(e) => setHasScope(e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                Has Scope/Work Description
              </span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={hasDeliverables}
                onChange={(e) => setHasDeliverables(e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                Requires Deliverables
              </span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={hasMilestones}
                onChange={(e) => setHasMilestones(e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                Requires Milestones
              </span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={hasPaymentSchedule}
                onChange={(e) => setHasPaymentSchedule(e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                Has Payment Schedule
              </span>
            </label>

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={hasLicensing}
                onChange={(e) => setHasLicensing(e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">
                Includes Product Licensing
              </span>
              <span className="ml-1 text-xs text-gray-500">
                (e.g., Fresh Intranet, Omnia, Orchestry)
              </span>
            </label>

            {hasPaymentSchedule && (
              <label className="flex items-center ml-6">
                <input
                  type="checkbox"
                  checked={supportsRecurringPayments}
                  onChange={(e) => setSupportsRecurringPayments(e.target.checked)}
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">
                  Supports Recurring Payments (licenses, subscriptions)
                </span>
              </label>
            )}

            <label className="flex items-center pt-2 border-t">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="w-4 h-4 text-primary-600 rounded"
              />
              <span className="ml-2 text-sm font-medium text-gray-700">
                Active (available for contract creation)
              </span>
            </label>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-xs text-blue-900">
            <strong>Tip:</strong> The contract wizard will automatically show/hide steps based on these settings.
          </p>
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={saving || uploading}
            className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Template'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving || uploading}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

